import React, { type JSX } from 'react';
import { observer } from 'mobx-react';
import { makeObservable } from 'mobx';
import { Scrollbars } from 'react-custom-scrollbars-4';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors, type DragEndEvent
} from '@dnd-kit/core';
import {
  SortableContext, sortableKeyboardCoordinates, useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { type NoteInfo } from '../../models/NoteInfo';
import { type CategoryInfo } from '../../models/CategoryInfo';
import { type AdvancedSearchResult } from '../../models/SearchResult';
import ContextMenu from '../ContextMenu';
import { Skeleton } from '../Skeleton';
import HighlightedText from '../HighlightedText';
import CategoryPicker from '../CategoryPicker';
import { ImgGear } from '../Images/ImgGear';
import { ImgArchive } from '../Images/ImgArchive';
import { ImgShield } from '../Images/ImgShield';

import './_sidebar.scss';
import './_rail.scss';

const untitledNoteTitle = 'Untitled note';

const HEX_COLOR_RE = /#[0-9A-Fa-f]{6}/;

/** Background tint for colored notes; the 3px selection lane (active note) uses
 *  --note-color / --cat-color, consumed in _sidebar.scss. */
const getNoteStyle = (note: NoteInfo, catColor?: string): React.CSSProperties => {
  const style: React.CSSProperties & Record<string, string> = {};
  if (catColor && catColor.match(HEX_COLOR_RE)) {
    style['--cat-color'] = catColor;
  }
  const color = note.color;
  if (color && color.match(HEX_COLOR_RE)) {
    style['--note-color'] = color;
    const r = parseInt(color.substring(1, 3), 16);
    const g = parseInt(color.substring(3, 5), 16);
    const b = parseInt(color.substring(5, 7), 16);
    style.backgroundColor = `rgba(${r}, ${g}, ${b}, 0.08)`;
  }
  return style;
};

/* ----------------------------------------
   Icons
   ---------------------------------------- */

function PinIcon({ size = 13, filled = false }: { size?: number, filled?: boolean }): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 17v5M9 3h6l-1 7 3 3H7l3-3-1-7z" />
    </svg>
  );
}

function SortIcon({ size = 13 }: { size?: number }): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 4v16M7 20l-3-3M7 4l3 3M17 20V4M17 4l3 3M17 20l-3-3" />
    </svg>
  );
}

/* ----------------------------------------
   SortableItem
   ---------------------------------------- */

function relTime(ts?: string): string {
  if (!ts) return '';
  const d = Date.now() - new Date(ts).getTime();
  if (isNaN(d)) return '';
  if (d < 60e3) return 'now';
  const m = Math.floor(d / 60e3);
  if (m < 60) return m + 'm';
  const h = Math.floor(m / 60);
  if (h < 24) return h + 'h';
  const days = Math.floor(h / 24);
  if (days < 7) return days + 'd';
  return new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

interface SortableItemProps {
  note: NoteInfo;
  categoryName: string;
  catColor?: string;
  // Hide the per-note category label inside a single category view (redundant there).
  showCategory: boolean;
  isDisabled: boolean;
  isActive: boolean;
  onMouseDown: (e: React.MouseEvent, note: NoteInfo) => void;
}

const SortableItem = React.memo<SortableItemProps>(function SortableItem({
  note, categoryName, catColor, showCategory, isDisabled, isActive, onMouseDown
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: note.id, disabled: isDisabled });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    ...getNoteStyle(note, catColor)
  };

  let className = 'note-item';
  if (isActive) className += ' active';
  if (note.isPinned) className += ' pinned';

  return (
    <li
      ref={setNodeRef}
      data-id={note.id}
      className={className}
      style={style}
      onMouseDown={(e) => onMouseDown(e, note)}
      {...attributes}
      {...listeners}
    >
      <div className="ni-top">
        {note.isPinned && <span className="ni-pin" title="Pinned"><PinIcon size={12} filled /></span>}
        {showCategory && categoryName && <span className="ni-cat">{categoryName}</span>}
        {note.isProtected && <span className="ni-protected"><ImgShield size={12} /></span>}
      </div>
      <div className="ni-title-row">
        <span className="ni-title">{note.name || untitledNoteTitle}</span>
        <span className="ni-meta">
          <span className="note-item-actions">
            <span className="note-action-btn context-menu-target">
              <ImgGear size={14} />
            </span>
          </span>
          <span className="ni-time">{relTime(note.lastWrite)}</span>
        </span>
      </div>
      {(note.isProtected || note.snippet) && (
        <div className={`ni-excerpt${note.isProtected ? ' ni-excerpt-blurred' : ''}`}>
          {note.isProtected
            ? 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore.'
            : note.snippet}
        </div>
      )}
    </li>
  );
});

/* ----------------------------------------
   SortableNotesList
   ---------------------------------------- */

interface SortableNotesListProps {
  notes: NoteInfo[];
  isDisabled: boolean;
  isTouchDevice: boolean;
  activeNoteId: string;
  categoryById: Record<string, string>;
  catColorById: Record<string, string>;
  showCategory: boolean;
  onMouseDown: (e: React.MouseEvent, note: NoteInfo) => void;
  onDragEnd: (event: DragEndEvent) => void;
}

const SortableNotesList = React.memo<SortableNotesListProps>(function SortableNotesList({
  notes, isDisabled, isTouchDevice, activeNoteId, categoryById, catColorById, showCategory, onMouseDown, onDragEnd
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10,
        delay: isTouchDevice ? 400 : 200,
        tolerance: 10
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={notes.map(n => n.id)} strategy={verticalListSortingStrategy}>
        <Scrollbars hideTracksWhenNotNeeded autoHide className="scrollbars-wrapper">
          <ul className="notes-list">
            {notes.map((note) => (
              <SortableItem
                key={note.id}
                note={note}
                categoryName={categoryById[note.categoryId ?? ''] ?? ''}
                catColor={catColorById[note.categoryId ?? ''] || undefined}
                showCategory={showCategory}
                isDisabled={isDisabled}
                isActive={note.id === activeNoteId}
                onMouseDown={onMouseDown}
              />
            ))}
          </ul>
        </Scrollbars>
      </SortableContext>
    </DndContext>
  );
});

/* ----------------------------------------
   NoteSidebar (exported class component)
   ---------------------------------------- */

export interface NoteSidebarProps {
  notes: NoteInfo[];
  categories: CategoryInfo[];
  isArchive: boolean;
  onlyPinned: boolean;
  sortNewest: boolean;
  activeCategoryId: string;
  activeNoteId: string;
  isMobileSidebarVisible: boolean;
  isTouchDevice: boolean;
  containerRef: React.RefObject<HTMLDivElement>;
  // True while the initial note list is still loading — shows a skeleton.
  isLoading: boolean;
  // Search mode (driven by the top search box)
  searchQuery: string;
  searchResults: AdvancedSearchResult[] | null;
  isSearching: boolean;
  onSelectSearchResult: (noteId: string) => void;
  onClearSearch: () => void;
  // Actions
  onArchiveToggle: () => void;
  onPinnedToggle: () => void;
  onNewestToggle: () => void;
  onNoteMouseDown: (e: React.MouseEvent, note: NoteInfo) => void;
  onCollapse: () => void;
  onCategoryChanged: (category: CategoryInfo) => void;
  onDragEnd: (event: DragEndEvent) => void;
  onRenderContextMenu: (element: HTMLElement) => JSX.Element | null;
}

@observer
export default class NoteSidebar extends React.Component<NoteSidebarProps> {
  constructor(props: NoteSidebarProps) {
    super(props);
    makeObservable(this);
  }

  /** Shimmer placeholder rows shown while the initial note list loads. */
  private _renderListSkeleton(): JSX.Element {
    return (
      <ul className="notes-list">
        {Array.from({ length: 6 }).map((_, i) => (
          <li key={i} className="note-skeleton-row">
            <div className="nsr-top">
              <Skeleton width={i % 3 === 0 ? 56 : 72} height={10} />
              <Skeleton width={28} height={10} />
            </div>
            <div className="nsr-title">
              <Skeleton width={`${i % 2 === 0 ? 78 : 64}%`} height={13} />
            </div>
            <Skeleton width={`${i % 2 === 0 ? 92 : 84}%`} height={11} />
          </li>
        ))}
      </ul>
    );
  }

  private _renderSearchResults(): JSX.Element {
    const { searchResults, isSearching, activeNoteId, onSelectSearchResult, categories, searchQuery } = this.props;

    if (isSearching) {
      return <div className="nl-empty">Searching…</div>;
    }
    if (!searchResults || searchResults.length === 0) {
      return <div className="nl-empty">No matching notes.</div>;
    }

    const terms = searchQuery.trim().split(/\s+/).filter(Boolean);
    const catColorById: Record<string, string> = {};
    categories.forEach(c => { catColorById[c.id] = c.color || ''; });

    return (
      <Scrollbars hideTracksWhenNotNeeded autoHide className="scrollbars-wrapper">
        <ul className="notes-list">
          {searchResults.map((r) => {
            let className = 'note-item search-result';
            if (r.noteId === activeNoteId) className += ' active';
            const style = getNoteStyle({ color: r.color ?? '' } as NoteInfo, catColorById[r.categoryId ?? ''] || undefined);
            return (
              <li key={r.noteId} className={className} style={style} onMouseDown={(e) => { if (e.button === 0) onSelectSearchResult(r.noteId); }}>
                <div className="ni-top">
                  {r.categoryName && <span className="ni-cat">{r.categoryName}</span>}
                  <span className="ni-time">{relTime(r.lastWrite ?? undefined)}</span>
                  {r.isArchived && <span className="ni-cat">archived</span>}
                  {r.isProtected && <span className="ni-protected"><ImgShield size={12} /></span>}
                  {r.semanticScore != null && r.semanticScore > 0 && (
                    <span className="ni-semantic" title="Semantic match">
                      <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l1.9 4.6L18.5 9.5l-4.6 1.9L12 16l-1.9-4.6L5.5 9.5l4.6-1.9L12 3z" /></svg>
                      {Math.round(r.semanticScore * 100)}%
                    </span>
                  )}
                </div>
                <div className="ni-title"><HighlightedText text={r.name || untitledNoteTitle} terms={terms} /></div>
                {r.snippets.length > 0 && (
                  <div className="ni-excerpt">
                    {r.snippets.map((s, i) => (
                      <span key={i} className="search-match"><HighlightedText text={s} terms={terms} /></span>
                    ))}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </Scrollbars>
    );
  }

  render(): JSX.Element {
    const {
      notes, categories, isArchive, onlyPinned, sortNewest,
      activeCategoryId, activeNoteId, isMobileSidebarVisible, isTouchDevice,
      containerRef, isLoading, searchQuery, searchResults, isSearching, onClearSearch,
      onArchiveToggle, onPinnedToggle, onNewestToggle,
      onNoteMouseDown, onCollapse, onCategoryChanged, onDragEnd, onRenderContextMenu
    } = this.props;

    const isSearchMode = searchResults !== null || isSearching;
    const activeCategory = categories.find(c => c.id === activeCategoryId);
    let listTitle = isArchive ? 'Archive' : (activeCategory?.name || 'All notes');
    // The mobile fly-out swaps the read-only title for a category picker.
    // Prepend a synthetic "All notes" row so the trigger reads correctly when no category is active.
    const pickerCategories: CategoryInfo[] = [
      { id: '', name: 'All notes', sortOrder: 0 },
      ...categories
    ];
    if (isSearchMode) listTitle = 'Search';
    else if (onlyPinned) listTitle = 'Pinned';
    const count = isSearchMode ? (searchResults?.length ?? 0) : notes.length;
    const categoryById: Record<string, string> = {};
    const catColorById: Record<string, string> = {};
    categories.forEach(c => {
      categoryById[c.id] = c.name || '';
      catColorById[c.id] = c.color || '';
      // The virtual Default category groups notes without a categoryId.
      if (c.id === 'default') {
        categoryById[''] = c.name || '';
        catColorById[''] = c.color || '';
      }
    });
    const dragDisabled = onlyPinned || sortNewest;
    // The per-note category label is only useful when notes from several categories are mixed.
    const showCategory = !isSearchMode && activeCategoryId === '';

    return (
      <section
        className={`note-list${isMobileSidebarVisible ? ' mobile-visible' : ''}`}
        ref={containerRef as unknown as React.RefObject<HTMLElement>}
      >
        <div className="nl-head">
          <div className={`nl-title-row${!isSearchMode ? ' has-picker' : ''}`}>
            <h2 className="nl-title-text">{listTitle}</h2>
            {!isSearchMode && (
              <div className="nl-category-picker">
                <CategoryPicker
                  categories={pickerCategories}
                  activeCategoryId={activeCategoryId}
                  onCategoryChanged={onCategoryChanged}
                />
              </div>
            )}
            <span className="nl-count">{count}</span>
            {isSearchMode && (
              <button className="icon-btn sm" title="Clear search" aria-label="Clear search" onClick={onClearSearch}>
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            )}
            <button className="icon-btn sm nl-toggle" title="Collapse list" aria-label="Collapse list" onClick={onCollapse}>
              <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><rect x="3" y="4" width="18" height="16" rx="2" /><line x1="9" y1="4" x2="9" y2="20" /></svg>
            </button>
          </div>

          {isSearchMode && searchQuery && (
            <div className="nl-search-echo">“{searchQuery}”</div>
          )}

          {!isSearchMode && (
            <div className="nl-tools">

              <button
                title={isArchive ? 'Showing archived notes' : 'Show archived notes'}
                className={`chip${isArchive ? ' on' : ''}`}
                onClick={onArchiveToggle}
              >
                <ImgArchive size={13} />
                Archive
              </button>
              <button
                title={onlyPinned ? 'Showing pinned notes' : 'Show pinned notes'}
                className={`chip${onlyPinned ? ' on' : ''}`}
                onClick={onPinnedToggle}
              >
                <PinIcon size={13} filled={onlyPinned} />
                Pinned
              </button>
              <button
                title={sortNewest ? 'Newest first (click to restore order)' : 'Sort by last update'}
                className={`chip${sortNewest ? ' on' : ''}`}
                onClick={onNewestToggle}
              >
                <SortIcon size={13} />
                {sortNewest ? 'Newest' : 'Sort'}
              </button>
            </div>
          )}
        </div>

        {/* Context menu (scoped to this container) */}
        <ContextMenu
          container={containerRef}
          onAppear={e => e.tagName !== 'INPUT'}
        >
          {(element) => onRenderContextMenu(element)}
        </ContextMenu>

        <div className="nl-scroll">
          {isLoading ? (
            this._renderListSkeleton()
          ) : isSearchMode ? (
            this._renderSearchResults()
          ) : notes.length === 0 ? (
            <div className="nl-empty">No notes here.</div>
          ) : (
            <SortableNotesList
              notes={notes}
              isDisabled={dragDisabled}
              isTouchDevice={isTouchDevice}
              activeNoteId={activeNoteId}
              categoryById={categoryById}
              catColorById={catColorById}
              showCategory={showCategory}
              onMouseDown={onNoteMouseDown}
              onDragEnd={onDragEnd}
            />
          )}
        </div>
      </section>
    );
  }
}
